// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// The panels shown inside the login card: PhoneStep, OtpStep, HelpStep and SignedInStep.
// Presentational — all state and API calls come in through props from pages/Welcome.tsx.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa6";
import { ArrowRight, ChevronRight, CircleAlert, Leaf, Loader2, MessageSquareText, Store } from "lucide-react";
import { COPY, HELP_TIPS, SUPPORT_LINK } from "../../data/welcome";
import { maskNationalNumber } from "../../utils/phone";
import type { LoginStatus } from "../../hooks/useOtpLogin";
import SuccessCheckmark from "../SuccessCheckmark";
import OtpInput from "./OtpInput";

const BUTTON_BASE =
  "flex w-full items-center justify-center gap-3 rounded-full py-4 text-lg font-semibold shadow-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-60";
const WHATSAPP_BUTTON = `${BUTTON_BASE} bg-whatsapp text-ocean-950 shadow-whatsapp/30 hover:bg-whatsapp-600`;
const HEADING = "font-display text-3xl font-bold tracking-tight text-ocean-950 dark:text-foam-50 sm:text-4xl";
const BODY = "mt-3 text-base leading-relaxed text-ocean-800/85 dark:text-foam-100/80";
const LINK_BUTTON =
  "inline-flex items-center gap-1.5 rounded text-sm font-semibold text-ocean-800 hover:text-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:text-foam-100";

/** 24 -> "0:24", 3541 -> "59:01" (an hourly-cap wait can be long). */
function formatCountdown(totalSeconds: number): string {
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

function ErrorText({ id, children }: { id: string; children: string | null }) {
  // Always rendered so the live region exists before an error appears (screen readers
  // only announce changes to a region that is already in the DOM).
  return (
    <p
      id={id}
      role="alert"
      className="mt-3 flex min-h-5 items-start gap-1.5 text-sm font-medium text-coral-500 dark:text-coral-300"
    >
      {/* An icon as well as colour, so the error isn't conveyed by colour alone. */}
      {children && <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
      <span>{children}</span>
    </p>
  );
}

/* ─────────────────────────── WhatsApp number ─────────────────────────── */

interface PhoneStepProps {
  nationalNumber: string;
  status: LoginStatus;
  error: string | null;
  onNumber: (value: string) => void;
  onSubmit: () => void;
  onGuest: () => void;
}

export function PhoneStep({ nationalNumber, status, error, onNumber, onSubmit, onGuest }: PhoneStepProps) {
  const sending = status === "sending";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!sending) onSubmit();
  };

  return (
    <form onSubmit={submit} noValidate>
      <h1 tabIndex={-1} className={`${HEADING} outline-none`}>Log in with your mobile number</h1>
      <p className={BODY}>Enter your mobile number. We&apos;ll text you a 6-digit code.</p>

      <label htmlFor="welcome-phone" className="mt-6 block text-sm font-semibold text-ocean-950 dark:text-foam-50">
        Mobile number
      </label>
      <div
        className={`mt-2 flex items-stretch overflow-hidden rounded-xl border bg-white focus-within:ring-2 focus-within:ring-teal-600 dark:bg-ocean-800 ${
          error ? "border-coral-500 dark:border-coral-300" : "border-ocean-800/55 dark:border-foam-100/50"
        }`}
      >
        <span className="flex items-center border-r border-ocean-900/10 bg-ocean-900/5 px-4 text-base font-semibold text-ocean-900 dark:border-white/15 dark:bg-white/10 dark:text-foam-100">
          +91
        </span>
        <input
          id="welcome-phone"
          name="phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="98765 43210"
          value={nationalNumber}
          onChange={(e) => onNumber(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby="welcome-phone-error welcome-phone-hint"
          readOnly={sending}
          className="min-w-0 flex-1 bg-transparent px-4 py-4 text-lg font-medium tracking-wide text-ocean-950 outline-none placeholder:text-ocean-800/60 dark:text-foam-50"
        />
      </div>
      <p id="welcome-phone-hint" className="mt-2 text-xs text-ocean-800/75 dark:text-foam-100/65">
        10 digits, for example 98765 43210. Standard SMS rates from your operator may apply.
      </p>
      <ErrorText id="welcome-phone-error">{error}</ErrorText>

      <button type="submit" aria-disabled={sending} className={`${WHATSAPP_BUTTON} mt-3`}>
        {sending ? (
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        ) : (
          <MessageSquareText className="h-6 w-6" aria-hidden="true" />
        )}
        {sending ? "Sending…" : "Send code by SMS"}
      </button>

      <p className="mt-4 text-center text-xs text-ocean-800/75 dark:text-foam-100/65">
        By continuing you agree to our{" "}
        <Link to="/terms" target="_blank" rel="noopener" className="font-semibold underline underline-offset-2">
          Terms<span className="sr-only"> (opens in a new tab)</span>
        </Link>{" "}
        and{" "}
        <Link to="/privacy" target="_blank" rel="noopener" className="font-semibold underline underline-offset-2">
          Privacy Policy<span className="sr-only"> (opens in a new tab)</span>
        </Link>
        .
      </p>

      <div className="my-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-ocean-800/70 dark:text-foam-100/60">
        <span aria-hidden="true" className="h-px flex-1 bg-ocean-900/10 dark:bg-white/15" />
        <Leaf className="h-3.5 w-3.5 text-teal-700" aria-hidden="true" />
        Or explore first
        <Leaf className="h-3.5 w-3.5 text-teal-700" aria-hidden="true" />
        <span aria-hidden="true" className="h-px flex-1 bg-ocean-900/10 dark:bg-white/15" />
      </div>

      <button
        type="button"
        onClick={onGuest}
        className="group flex w-full items-center gap-4 rounded-2xl bg-teal-100/70 p-5 text-left transition-colors hover:bg-teal-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-teal-700/20 dark:hover:bg-teal-700/30"
      >
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white text-teal-700 shadow-sm dark:bg-ocean-800">
          <Store className="h-6 w-6" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-xl font-semibold text-teal-700 dark:text-teal-200">{COPY.guestTitle}</span>
          <span className="mt-1 block text-sm text-ocean-800/85 dark:text-foam-100/80">{COPY.guestBody}</span>
        </span>
        <ChevronRight
          className="h-5 w-5 shrink-0 text-ocean-800 transition-transform group-hover:translate-x-0.5 dark:text-foam-100"
          aria-hidden="true"
        />
      </button>
    </form>
  );
}

/* ───────────────────────────── Verify code ───────────────────────────── */

interface OtpStepProps {
  nationalNumber: string;
  status: LoginStatus;
  error: string | null;
  secondsLeft: number;
  onVerify: (code: string) => void;
  onResend: () => void;
  onChangeNumber: () => void;
  onClearError: () => void;
}

export function OtpStep({ nationalNumber, status, error, secondsLeft, onVerify, onResend, onChangeNumber, onClearError }: OtpStepProps) {
  const [code, setCode] = useState("");
  const verifying = status === "verifying";
  const sending = status === "sending";
  const previous = useRef(error);
  const boxes = useRef<HTMLDivElement>(null);
  const wasCounting = useRef(secondsLeft > 0);

  const focusFirstBox = () => boxes.current?.querySelector("input")?.focus();

  // A wrong code is cleared so the visitor can retype immediately, with focus back on box 1.
  useEffect(() => {
    if (error && error !== previous.current) {
      setCode("");
      focusFirstBox();
    }
    previous.current = error;
  }, [error]);

  // A fresh code has been sent: the Resend button just unmounted, so put focus somewhere
  // sensible (the first box) instead of letting it fall to <body>.
  useEffect(() => {
    if (secondsLeft > 0 && !wasCounting.current) focusFirstBox();
    wasCounting.current = secondsLeft > 0;
  }, [secondsLeft]);

  if (status === "success") {
    return (
      <div className="flex flex-col items-center py-10 text-center" role="status">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-teal-100 text-teal-700">
          <SuccessCheckmark className="h-10 w-10" />
        </span>
        <h1 tabIndex={-1} className={`${HEADING} mt-6 outline-none`}>You&apos;re in!</h1>
        <p className={BODY}>Taking you to the store…</p>
      </div>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!verifying) onVerify(code); // too few digits -> the hook shows an inline error
  };

  return (
    <form onSubmit={submit} noValidate>
      <h1 tabIndex={-1} className={`${HEADING} outline-none`}>Enter your code</h1>
      <p className={BODY}>
        We texted a 6-digit code to <strong className="whitespace-nowrap">{maskNationalNumber(nationalNumber)}</strong>.
      </p>

      <div className="mt-6" ref={boxes}>
        <OtpInput
          value={code}
          onChange={(value) => {
            setCode(value);
            if (error) onClearError();
          }}
          onComplete={onVerify}
          busy={verifying}
          invalid={Boolean(error)}
          describedBy="welcome-otp-error"
        />
      </div>
      <ErrorText id="welcome-otp-error">{error}</ErrorText>

      <button type="submit" aria-disabled={verifying} className={`${WHATSAPP_BUTTON} mt-3`}>
        {verifying && <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />}
        {verifying ? "Checking…" : "Verify and continue"}
      </button>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm">
        {secondsLeft > 0 ? (
          <p className="text-ocean-800/80 dark:text-foam-100/70" aria-live="off">
            Didn&apos;t get it? Resend in <span className="font-semibold tabular-nums">{formatCountdown(secondsLeft)}</span>
          </p>
        ) : (
          <button type="button" onClick={() => !sending && onResend()} aria-disabled={sending} className={LINK_BUTTON}>
            {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {sending ? "Sending…" : "Resend code"}
          </button>
        )}
        <button type="button" onClick={onChangeNumber} className={LINK_BUTTON}>
          Change number
        </button>
      </div>
    </form>
  );
}

/* ─────────────────────────── Help & Concierge ─────────────────────────── */

export function HelpStep({ onGuest }: { onGuest: () => void }) {
  return (
    <div>
      <h1 tabIndex={-1} className={`${HEADING} outline-none`}>Help &amp; Concierge</h1>
      <p className={BODY}>Stuck signing in? Here&apos;s what usually helps — or talk to a person.</p>

      <dl className="mt-5 space-y-4">
        {HELP_TIPS.map((tip) => (
          <div key={tip.title} className="rounded-xl bg-ocean-700/5 p-4 dark:bg-white/10">
            <dt className="text-sm font-bold text-ocean-950 dark:text-foam-50">{tip.title}</dt>
            <dd className="mt-1 text-sm leading-relaxed text-ocean-800/85 dark:text-foam-100/75">{tip.body}</dd>
          </div>
        ))}
      </dl>

      <a href={SUPPORT_LINK} target="_blank" rel="noreferrer" className={`${WHATSAPP_BUTTON} mt-6`}>
        <FaWhatsapp className="h-7 w-7" aria-hidden="true" />
        Chat with our concierge
        <span className="sr-only">(opens WhatsApp in a new tab)</span>
      </a>
      <div className="mt-5 text-center">
        <button type="button" onClick={onGuest} className={LINK_BUTTON}>
          Explore the store as a guest
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────────── Already signed in ───────────────────────────── */

interface SignedInStepProps {
  phone: string | null;
  error: string | null;
  onContinue: () => void;
  onSignOut: () => void;
}

export function SignedInStep({ phone, error, onContinue, onSignOut }: SignedInStepProps) {
  return (
    <div>
      <h1 tabIndex={-1} className={`${HEADING} outline-none`}>You&apos;re signed in</h1>
      <p className={BODY}>{phone ? `Signed in as ${phone}.` : "Welcome back."} Your orders and favourites are ready.</p>
      <button type="button" onClick={onContinue} className={`${BUTTON_BASE} mt-7 bg-teal-700 text-white shadow-teal-700/25 hover:opacity-90`}>
        Continue to the store
        <ArrowRight className="h-5 w-5" aria-hidden="true" />
      </button>
      <p className="mt-4 text-center text-sm">
        <Link to="/account/addresses" className="font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">
          Manage my delivery addresses
        </Link>
      </p>
      <ErrorText id="welcome-signout-error">{error}</ErrorText>
      <div className="mt-2 text-center">
        <button type="button" onClick={onSignOut} className={LINK_BUTTON}>
          Sign out
        </button>
      </div>
    </div>
  );
}
